import { useMemo } from 'react';

import { useFeatureFlags } from './FeatureFlagProvider';
import { ProviderPortalFeatureFlagKey } from './providerPortalFeatureFlags';

/**
 * Tag any item (table column, nav link, menu item, etc.) with `featureFlag` to make it
 * disappear from {@link useFeatureFlagFilter} output while the flag is disabled. Set
 * `featureFlagNegate` when the flag's name is phrased as a negative (e.g. `..._DISABLED`),
 * so the item disappears while the flag is *enabled* instead.
 *
 * When the flag is retired, simply delete the `featureFlag`/`featureFlagNegate` properties
 * from the tagged item(s) - no other code needs to change, since untagged items always pass
 * the filter.
 */
export type FeatureFlaggable<T> = T & { featureFlag?: ProviderPortalFeatureFlagKey; featureFlagNegate?: boolean };

/**
 * Filters out items whose `featureFlag` is currently disabled (or, if `featureFlagNegate`
 * is set, currently enabled). Items without a `featureFlag` are always kept.
 */
export const useFeatureFlagFilter = <T,>(items: Readonly<FeatureFlaggable<T>[]>): Readonly<FeatureFlaggable<T>[]> => {
    const { flags } = useFeatureFlags();
    return useMemo(
        () => items.filter((item) => {
            if (!item.featureFlag) {
                return true;
            }
            const flagValue = flags[item.featureFlag] ?? false;
            return item.featureFlagNegate ? !flagValue : flagValue;
        }),
        [items, flags]
    );
};
